import { useModal } from "@ebay/nice-modal-react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldClaimMapping from "components/FormFields/ClaimMapping";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import setLocation from "shared/setLocation";
import { stringIsRequired, validateRequiredURL } from "utils/formValidators";
import { getDrawerFormFix } from "utils/css";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";

import SettingsSingleSignOnConfigInfoOIDC from "../../components/ConfigInfoOIDC";
import { CREATE_SSO_OIDC } from "./gql";
import { formDefaultValues, type SettingsSingleSignOnOIDCFormFields } from "./helpers";

const useOIDCCreate = createDefaultMutationHook(CREATE_SSO_OIDC);

type SettingsSingleSignOnCreateOIDCProps = {
  setIsDirty: (isDirty: boolean) => void;
};

const SettingsSingleSignOnCreateOIDC = ({ setIsDirty }: SettingsSingleSignOnCreateOIDCProps) => {
  const modal = useModal();

  const { reportSuccess } = useTypedContext(FlashContext);

  const [createOIDC, { loading }] = useOIDCCreate({
    refetchQueries: ["GetSSOSettings"],
  });

  const form = useForm<SettingsSingleSignOnOIDCFormFields>({
    mode: "onChange",
    defaultValues: formDefaultValues,
  });

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
  } = form;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationOIDC,

    defaultCallbackTrackProperties: {
      method: "OIDC",
    },
  });

  const onSubmit = handleSubmit((data) => {
    trackSegmentAnalyticsEvent("Set Up Save");

    const filteredClaimMapping = data.claimMapping.filter(
      (claim) => claim.name.trim() && claim.value.trim()
    );

    createOIDC(
      {
        clientId: data.clientId,
        clientCredentials: data.clientCredentials,
        identityProviderHost: data.identityProviderHost,
        claimMapping: filteredClaimMapping.length > 0 ? { entries: filteredClaimMapping } : null,
      },
      (data) => {
        if (data?.oidcCreate) {
          // FYI: `/oidc/start` is BE endpoint, do not replace it with "useNavigate", it works only with navigating to the external URL
          setLocation("/oidc/start");
          reportSuccess({ message: "OIDC successfully enabled" });
          modal.hide();
        }
      }
    );
  });

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} {...getDrawerFormFix()}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Set up OIDC" />
          <DrawerCloseIcon />
        </DrawerHeader>

        <DrawerBody noPadding>
          <DrawerSection gap="x-large">
            <SettingsSingleSignOnConfigInfoOIDC />
          </DrawerSection>
          <DrawerSection gap="x-large">
            <FormField label="Client ID" error={errors?.clientId?.message} noMargin>
              {({ ariaInputProps }) => (
                <Input
                  error={!!errors?.clientId}
                  {...register("clientId", {
                    validate: stringIsRequired(),
                    setValueAs: (value: string) => value.trim(),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField label="Secret" error={errors?.clientCredentials?.message} noMargin>
              {({ ariaInputProps }) => (
                <SecretInput
                  error={!!errors?.clientCredentials}
                  {...register("clientCredentials", {
                    validate: stringIsRequired(),
                    setValueAs: (value: string) => value.trim(),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField label="Provider URL" error={errors?.identityProviderHost?.message} noMargin>
              {({ ariaInputProps }) => (
                <Input
                  type="url"
                  placeholder="https://"
                  error={!!errors?.identityProviderHost}
                  {...register("identityProviderHost", {
                    validate: validateRequiredURL(),
                    setValueAs: (value: string) => value.trim(),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          </DrawerSection>
          <DrawerSection>
            <FormFieldClaimMapping />
          </DrawerSection>
        </DrawerBody>
        <DrawerFooter fullWidth>
          <DrawerFooterActions>
            <DrawerCancelButton />
            <Button variant="primary" type="submit" loading={loading}>
              Save
            </Button>
          </DrawerFooterActions>
        </DrawerFooter>
      </form>
    </FormProvider>
  );
};

export default SettingsSingleSignOnCreateOIDC;
